<template>
  <div class="material-selection-section oring-card">
    <div class="section-header">
      <h3 class="h4 section-title">
        {{ $tv('selectMaterialOptional', 'ORingConfigurator') }}
      </h3>
      <p class="section-description">
        {{ $tv('selectMaterialText', 'ORingConfigurator') }}
      </p>
    </div>

    <div class="filter-section">
      <div class="filter-columns">
        <div class="filter-controls">
          <div
            class="filter-group"
            :class="{ 'is-enabled': true }"
          >
            <label
              class="filter-label"
              for="elastomer-dropdown"
            >
              {{ $tv('Frontend.Parameter.Elastomer', 'ORingConfiguratorImport') }}
            </label>
            <DropDown
              id="elastomer-dropdown"
              v-model="elastomerFilter"
              :data-items="elastomerOptions"
              :default-item="{text: $tv('select', 'ORingConfigurator'), value: null}"
              :text-field="'text'"
              :value-field="'value'"
            />
          </div>

          <div
            class="filter-group"
            :class="{ 'is-enabled': isShoreHardnessEnabled }"
          >
            <label
              class="filter-label"
              for="shore-hardness-dropdown"
            >
              {{ $tv('Frontend.Parameter.ShoreHardness', 'ORingConfiguratorImport') }}
            </label>
            <DropDown
              id="shore-hardness-dropdown"
              v-model="shoreHardnessFilter"
              :data-items="shoreHardnessOptions"
              :default-item="{text: $tv('select', 'ORingConfigurator'), value: null}"
              :text-field="'text'"
              :value-field="'value'"
              :disabled="!isShoreHardnessEnabled"
            />
          </div>

          <div
            class="filter-group"
            :class="{ 'is-enabled': isCompoundEnabled }"
          >
            <label
              class="filter-label"
              for="compound-dropdown"
            >
              {{ $tv('Frontend.Parameter.Compound', 'ORingConfiguratorImport') }}
            </label>
            <DropDown
              id="compound-dropdown"
              v-model="compoundFilter"
              :data-items="compoundOptions"
              :default-item="{text: $tv('select', 'ORingConfigurator'), value: null}"
              :text-field="'text'"
              :value-field="'value'"
              :disabled="!isCompoundEnabled"
            />
          </div>
        </div>
        <div class="filter-action-container">
          <div class="filter-actions">
            <ReactiveButton
              :theme-color="'base'"
              :disabled="!hasActiveFilters"
              @click="resetFilters"
            >
              {{ $tv('resetFilters', 'ORingConfigurator') }}
            </ReactiveButton>
            <ReactiveButton
              :theme-color="'base'"
              :disabled="!hasBookmarkedMaterials"
              @click="importMaterials"
            >
              <ActionBookmark class="bookmark-icon" />
              {{ $tv('importFromBookmark', 'ORingConfigurator') }}
            </ReactiveButton>
          </div>
        </div>
      </div>
    </div>

    <div class="results-summary">
      <span class="results-count">
        {{ selectedMaterialIds.length }} {{ $tv('materialsSelected', 'ORingConfigurator') }}
      </span>
    </div>

    <div class="materials-table-container">
      <div
        v-if="loading"
        class="loading-state"
      >
        {{ $tv('Frontend.Text.MaterialsAreLoading', 'ORingConfiguratorImport') }}
      </div>

      <div
        v-else-if="filteredMaterials.length === 0"
        class="empty-state"
      >
        {{ $tv('Frontend.Text.NoResults', 'ORingConfiguratorImport') }}
      </div>

      <table
        v-else
        class="materials-table"
      >
        <tbody>
          <tr
            v-for="material in filteredMaterials"
            :key="material.basicMaterialId"
            class="material-row"
            :class="{ 'selected': isSelected(material.basicMaterialId) }"
            @click="toggleMaterial(material.basicMaterialId)"
          >
            <td
              class="select-column"
              @click.stop
            >
              <Checkbox
                :checked="isSelected(material.basicMaterialId)"
                @change="toggleMaterial(material.basicMaterialId)"
              />
            </td>
            <td class="elastomer-column">
              {{ material.elastomer }}
            </td>
            <td class="hardness-column">
              {{ material.shoreHardness }}
            </td>
            <td class="compound-column">
              {{ material.compoundNo }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Material Assistant Info -->
    <div class="material-assistant-info">
      <HtmlMessageContainer :is-info="true">
        <div class="inner-container">
          <Markdown
            class="content"
            :text="$tv('notSureWhatMaterials', 'ORingConfigurator')"
          />
          <div class="assistant-cta">
            <ReactiveButton
              :theme-color="'base'"
              @click="goToMaterialAssistant"
            >
              {{ $tv('goToMaterialAssistant', 'ORingConfigurator') }}
              <MdArrowForward class="forward-icon" />
            </ReactiveButton>
          </div>
        </div>
      </HtmlMessageContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {PropType} from 'vue'
import DropDown from '~/components/input-components/DropDown.vue'
import ReactiveButton from '~/components/ReactiveButton.vue'
import Checkbox from '~/components/input-components/Checkbox.vue'
import HtmlMessageContainer from '~/components/HtmlMessageContainer.vue'
import useI18nExtended from '~/composables/usei18nExtended'
import Markdown from '~/components/Markdown.vue'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import type {MaterialSearchItem, ORingGetMaterialSearchMaterialAssistantResponse} from '~/types/oring-api-responses'
import ActionBookmark from '~/assets/icons/action_bookmark.svg?component'
import MdArrowForward from '~/assets/icons/md_arrow_forward.svg?component'
import {useORingCalculatorStore} from '~/store/oring-calculator'

const i18n = useI18nExtended()
const oringStore = useORingCalculatorStore()
const {
  oringBookmarkedMaterialIds,
  oringMaterialSelectionElastomerFilter,
  oringMaterialSelectionShoreHardnessFilter,
  oringMaterialSelectionCompoundFilter
} = storeToRefs(oringStore)
const route = useRoute()
const app = useNuxtApp()

const props = defineProps({
  modelValue: {
    type: Array as PropType<number[]>,
    default: () => []
  }
})

const emits = defineEmits(['update:modelValue', 'bookmark-materials'])

function goToMaterialAssistant() {
  const sourcePage = route.path.includes('/design-cockpit') ? 'design-cockpit' : 'oring-assistant'
  oringStore.navigateToMaterialAssistant(sourcePage)
  navigateTo(app.$globalization.localePath('/oring-calculator/material-assistant', i18n.locale.value, app.$globalization.getRegion()))
}

const oringService = useOringCalculatorService()

const loading = ref(false)
const allMaterials = ref<MaterialSearchItem[]>([])
const selectedMaterialIds = ref<number[]>([...props.modelValue])
const manuallyDeselectedIds = ref<Set<number>>(new Set())

// Use store-backed refs for filters to persist across navigation
const elastomerFilter = oringMaterialSelectionElastomerFilter
const shoreHardnessFilter = oringMaterialSelectionShoreHardnessFilter
const compoundFilter = oringMaterialSelectionCompoundFilter

const elastomerOptions = ref<{text: string; value: string | null}[]>([])
const shoreHardnessOptions = ref<{text: string; value: string | null}[]>([])
const compoundOptions = ref<{text: string; value: string | null}[]>([])

const filteredMaterials = computed(() => {
  return materialsAfterHardnessFilter.value.filter(material => {
    const compoundMatch = !compoundFilter.value || compoundFilter.value.value === null || String(material.compoundNo) === String(compoundFilter.value.value)
    return compoundMatch
  })
})

const hasActiveFilters = computed(() => {
  const anyFilter = !!(elastomerFilter.value || shoreHardnessFilter.value || compoundFilter.value)
  const anySelection = selectedMaterialIds.value.length > 0
  return anyFilter || anySelection
})

const hasBookmarkedMaterials = computed(() => {
  return oringBookmarkedMaterialIds.value && oringBookmarkedMaterialIds.value.length > 0
})

const isShoreHardnessEnabled = computed(() => {
  return !!(elastomerFilter.value && elastomerFilter.value.value !== null)
})

const isCompoundEnabled = computed(() => {
  return !!(shoreHardnessFilter.value && shoreHardnessFilter.value.value !== null)
})

const materialsAfterElastomerFilter = computed(() => {
  if (!allMaterials.value || allMaterials.value.length === 0) {
    return []
  }

  if (!elastomerFilter.value || elastomerFilter.value.value === null) {
    return allMaterials.value
  }

  return allMaterials.value.filter(material =>
    String(material.elastomer) === String(elastomerFilter.value!.value)
  )
})

const materialsAfterHardnessFilter = computed(() => {
  if (!shoreHardnessFilter.value || shoreHardnessFilter.value.value === null) {
    return materialsAfterElastomerFilter.value
  }

  const selectedHardness = parseFloat(shoreHardnessFilter.value.value as string)
  if (isNaN(selectedHardness)) {
    return materialsAfterElastomerFilter.value
  }

  const minHardness = selectedHardness - 4.9999999
  const maxHardness = selectedHardness + 4.9999999

  return materialsAfterElastomerFilter.value.filter(material => {
    const materialHardness = parseFloat(material.shoreHardness)
    if (isNaN(materialHardness)) {
      return false
    }
    return materialHardness >= minHardness && materialHardness <= maxHardness
  })
})

async function loadMaterials(materialId = 0) {
  loading.value = true
  try {
    const response: ORingGetMaterialSearchMaterialAssistantResponse = await oringService.getMaterialSearchMaterialAssistant({savedMaterialId: materialId})

    allMaterials.value = response.materials ?? []

    buildFilterOptions()
  } catch (error) {
    allMaterials.value = []
  } finally {
    loading.value = false
  }
}

function buildFilterOptions() {
  const elastomers = [...new Set(allMaterials.value.map(m => m.elastomer))].filter(Boolean).sort()
  elastomerOptions.value = [
    ...elastomers.map(elastomer => ({
      text: elastomer,
      value: elastomer
    }))
  ]

  const hardnesses = [...new Set(materialsAfterElastomerFilter.value.map(m => m.shoreHardness))]
    .filter(Boolean)
    .filter(hardness => {
      const numericValue = parseFloat(hardness)
      return !isNaN(numericValue) && numericValue % 5 === 0
    })
    .sort((a, b) => parseFloat(a) - parseFloat(b))

  shoreHardnessOptions.value = [
    ...hardnesses.map(hardness => ({
      text: `${hardness} ± 5`,
      value: hardness
    }))
  ]

  const compounds = [...new Set(materialsAfterHardnessFilter.value.map(m => m.compoundNo))].filter(Boolean).sort()
  compoundOptions.value = [
    ...compounds.map(compound => ({
      text: compound,
      value: compound
    }))
  ]
}

function resetFilters() {
  elastomerFilter.value = null
  shoreHardnessFilter.value = null
  compoundFilter.value = null
  selectedMaterialIds.value = []
  manuallyDeselectedIds.value.clear()
  buildFilterOptions()
}

function isSelected(materialId: number): boolean {
  return selectedMaterialIds.value.includes(materialId)
}

function toggleMaterial(materialId: number) {
  const index = selectedMaterialIds.value.indexOf(materialId)
  if (index > -1) {
    selectedMaterialIds.value.splice(index, 1)
    // Track manual deselection
    manuallyDeselectedIds.value.add(materialId)
  } else {
    selectedMaterialIds.value.push(materialId)
    // Remove from manually deselected if re-selecting
    manuallyDeselectedIds.value.delete(materialId)
  }
  emits('update:modelValue', selectedMaterialIds.value)
}

function importMaterials() {
  emits('bookmark-materials')
}

watch(() => props.modelValue, (newValue) => {
  selectedMaterialIds.value = [...newValue]
})

watch(elastomerFilter, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    shoreHardnessFilter.value = null
    compoundFilter.value = null
    buildFilterOptions()
  }
})

watch(shoreHardnessFilter, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    compoundFilter.value = null
    buildFilterOptions()
  }
})

watch(() => filteredMaterials.value, (newFilteredMaterials) => {
  const filteredIds = newFilteredMaterials.map(m => m.basicMaterialId)

  // Remove selected items that are no longer in the filtered results
  selectedMaterialIds.value = selectedMaterialIds.value.filter(id => filteredIds.includes(id))

  if (newFilteredMaterials.length === 1) {
    // Auto-select when only 1 item remains, but only if not manually deselected
    const materialId = newFilteredMaterials[0].basicMaterialId
    if (!selectedMaterialIds.value.includes(materialId) && !manuallyDeselectedIds.value.has(materialId)) {
      selectedMaterialIds.value = [materialId]
    }
  } else if (newFilteredMaterials.length > 0 && newFilteredMaterials.length < allMaterials.value.length) {
    // Auto-select all filtered items when we have a subset, but exclude manually deselected ones
    const idsToSelect = filteredIds.filter(id => !manuallyDeselectedIds.value.has(id))
    selectedMaterialIds.value = idsToSelect
  }

  emits('update:modelValue', selectedMaterialIds.value)
})

onMounted(() => {
  loadMaterials()
})
</script>
<style lang="scss">
.material-selection-section {
  @include breakpoint-down($md) {
    .dropdown-wrapper,
    .k-dropdownlist.k-picker {
      // Mobile: dropdowns should fill their grid cell
      width: 100%;
    }
  }
}
</style>
<style lang="scss" scoped>
.material-selection-section {
  margin-bottom: 1rem;

  &.oring-card {
    max-width: 100%;
  }
}

.section-header {
  margin-bottom: 1rem;

  .section-title {
    margin: 0 0 1rem;
  }

  .section-description {
    margin: 0;
  }
}

.filter-section {
  margin-bottom: 1rem;

  .filter-columns {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    @include breakpoint-down($md) {
      flex-direction: column;
      gap: 1rem;
    }
  }

  .filter-title {
    margin: 0 0 1rem;
  }

  .filter-controls {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 36rem;
    gap: .75rem;
    align-items: end;
    margin-left: 1.75rem;

    @include breakpoint-down($md) {
      /* Mobile: show first two filters side by side, third takes full width */
      grid-template-columns: repeat(2, minmax(0, 1fr));
      width: 100%;
      margin-left: 0;

      /* Ensure grid children can shrink and not overflow columns */
      .filter-group { min-width: 0; }

      /* Make sure dropdowns fully fill their grid cells */
      :deep(.dropdown-wrapper),
      :deep(.k-dropdown),
      :deep(.k-dropdownlist.k-picker) {
        width: 100%;
        box-sizing: border-box;
      }

      /* Ensure the third filter spans the full row on mobile */
      .filter-group:nth-child(3) {
        grid-column: 1 / -1;
      }
    }
  }

  .filter-group {
    .filter-label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: $base-text-font-weight-light;
      color: $color__fst__gray__030;
    }

    &.is-enabled .filter-label {
      color: $color__text__default !important;
    }

    :deep(.k-dropdown) {
      .k-input-inner {
        font-weight: $base-text-font-weight-semi-bold;
        color: $color__text__default;
      }

      &[disabled] {
        .k-input-inner {
          font-weight: $base-text-font-weight-light;
          color: $color__fst__gray__030;
        }
      }
    }

    :deep(.k-list-item) {
      font-weight: $base-text-font-weight-semi-bold;
    }
  }

  .filter-action-container {
    position: relative;
  }

  .filter-actions {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    left: .5rem;

    @include breakpoint-up($lg) {
      position: absolute;
    }

    @include breakpoint-down($lg) {
      margin-top: 0;
    }

    .bookmark-icon {
      color: $color__white;
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0.5rem;
      flex-shrink: 0;
    }

    button {
      flex: 0 0 auto;
      white-space: nowrap;
      min-width: 0;
    }
  }
}

.results-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  .results-count {
    font-weight: 600;
  }
}

.materials-table-container {
  margin-bottom: 2rem;
  max-width: 38.375rem; /* 614px converted to rem */
  height: 15.625rem; /* make the container the scroll area */
  overflow: hidden auto;
  border-top: .0625rem solid $color__primary;
  border-bottom: .0625rem solid $color__primary;

  .loading-state,
  .empty-state {
    text-align: center;
    padding: 3rem;
    font-style: italic;
  }

  .materials-table {
    width: 100%;
    display: table;
    border-collapse: collapse;

    /* Custom scrollbar styling is now applied on the scroll container below */
  }

  &::-webkit-scrollbar {
    width: .625rem;
  }

  &::-webkit-scrollbar-track {
    background-color: $color__page--background;
  }

  &::-webkit-scrollbar-thumb {
    background: $color__primary;
    min-height: 5rem;
  }

  scrollbar-width: .3rem;

  .materials-table tbody {
    width: 100%;
    table-layout: fixed;
  }

  .materials-table .material-row {
    cursor: pointer;
  }

  .materials-table td {
    padding: .5rem;
    text-align: left;
    line-height: 2.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .materials-table .select-column {
    width: 1rem;
    text-align: center;
    padding: 0 .5rem 0 0;
    border-bottom: .125rem solid $color__input__border;
  }

  /* Column paddings and borders */
  .materials-table .elastomer-column {
    padding-left: .75rem;
    border-bottom: .125rem solid $color__input__border;
  }

  .materials-table .hardness-column {
    padding-left: 1.125rem;
    border-bottom: .125rem solid $color__input__border;
  }

  .materials-table .compound-column {
    padding-left: 1.125rem;
    border-bottom: .125rem solid $color__input__border;
  }

  /* Desktop default: three equal data columns of remaining width */
  .materials-table .elastomer-column,
  .materials-table .hardness-column,
  .materials-table .compound-column {
    width: 33%;
  }

  /* Mobile-only widths: first two 25%, third 50% of remaining width */
  @include breakpoint-down($md) {
    .materials-table .elastomer-column { width: 25%; }
    .materials-table .hardness-column { width: 25%; }
    .materials-table .compound-column { width: 50%; }
  }
}

.material-assistant-info {
  /* Align (i) info icon to top in this context */
  @include breakpoint-down($md) {
    :deep(.warningcontainer--item) {
      align-items: flex-start;
    }
  }

  .inner-container {
    display: flex;
    flex: 1 0 50%;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;

    @include breakpoint-down($md) {
      flex-direction: column;
      gap: .5rem;
    }

    .forward-icon {
      width: 1.25rem;
      height: 1.25rem;
      margin-left: 0.25rem;
      flex-shrink: 0;
    }

    .content {
      flex: 1 0 0;

      :deep(strong) {
        font-weight: 600 !important;
      }

      :deep(p) {
        margin: 0;
        margin-block: 0 !important;
      }
    }

    > div {
      flex: 0 0 auto;
    }

    /* Make CTA button match content width on small screens */
    @include breakpoint-down($sm) {
      .assistant-cta {
        width: 100%;

        :deep(button),
        :deep(.reactive-button) {
          width: 100%;
          justify-content: center; /* keep text/icon centered */
        }
      }
    }
  }
}
</style>
